import React, { useEffect, useState } from 'react';
import { Input, Row, Col, Typography, Select, Alert } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';

const { Text } = Typography;
const { Option } = Select;

const TIME_UNITS = {
  minutes: { label: 'minutes', multiplier: 60, min: 2, max: 59 },
  hours: { label: 'hours', multiplier: 3600, min: 1, max: 23 },
  days: { label: 'days', multiplier: 86400, min: 1, max: 7 },
};

const MIN_SECONDS_ABSOLUTE = 120; // 2 minutes
const MAX_SECONDS_ABSOLUTE = 7 * 24 * 60 * 60; // 7 days

const FollowUps = ({ data, onChange, node }) => {
  const [timeValue, setTimeValue] = useState(2);
  const [timeUnit, setTimeUnit] = useState('minutes');
  const [error, setError] = useState('');

  // Convert seconds to appropriate time value and unit (without seconds option)
  const convertSecondsToTimeValue = (totalSeconds) => {
    if (!totalSeconds) return { value: 2, unit: 'minutes' };
    if (totalSeconds < MIN_SECONDS_ABSOLUTE) return { value: 2, unit: 'minutes' };

    const units = ['days', 'hours', 'minutes'];
    for (const unit of units) {
      const { multiplier, min, max } = TIME_UNITS[unit];
      const value = totalSeconds / multiplier;
      if (value >= min && value <= max && Number.isInteger(value)) {
        return { value, unit };
      }
    }

    const minuteValue = Math.round(totalSeconds / 60);
    return {
      value: Math.max(TIME_UNITS.minutes.min, Math.min(TIME_UNITS.minutes.max, minuteValue)),
      unit: 'minutes',
    };
  };

  // Initialize with data from props and keep in sync when delay changes
  useEffect(() => {
    const delay = data?.operations?.delay;

    if (delay !== undefined) {
      const normalized = Math.max(MIN_SECONDS_ABSOLUTE, Math.min(MAX_SECONDS_ABSOLUTE, delay));
      const { value, unit } = convertSecondsToTimeValue(normalized);
      setTimeValue(value);
      setTimeUnit(unit);

      // If incoming delay is outside allowed range, sync fixed value back
      if (normalized !== delay) {
        handleChange(normalized);
      }
    } else {
      // Default to 1 hour
      setTimeValue(1);
      setTimeUnit('hours');
      handleChange(3600);
    }
    // Only depends on the external delay value; no eslint rule directives used.
  }, [data?.operations?.delay]); // eslint rule removed as requested

  const validateTime = (value, unit) => {
    const { min, max, multiplier } = TIME_UNITS[unit];
    const totalSeconds = value * multiplier;

    if (value < min || value > max) {
      return `Value must be between ${min} and ${max} ${unit}`;
    }
    if (totalSeconds < MIN_SECONDS_ABSOLUTE) {
      return 'Minimum wait time is 2 minutes';
    }
    if (totalSeconds > MAX_SECONDS_ABSOLUTE) {
      return 'Maximum wait time is 7 days';
    }
    return '';
  };

  const handleChange = (totalSeconds) => {
    const payload = {
      key: 'data',
      data: {
        type: node?.type?.toLowerCase(),
        [`${node?.type?.toLowerCase()}`]: {
          delay: totalSeconds,
        },
        operations: {
          delay: totalSeconds,
        },
      },
    };

    onChange({
      type: 'UPDATE_NODE',
      id: node?.uniq_id,
      ...payload,
    });
  };

  const handleTimeValueChange = (value) => {
    const fallback = timeUnit === 'minutes' ? 2 : 1;
    const numValue = Number.isNaN(parseInt(value, 10)) ? fallback : parseInt(value, 10);
    setTimeValue(numValue);

    const err = validateTime(numValue, timeUnit);
    setError(err);

    if (!err) {
      const totalSeconds = numValue * TIME_UNITS[timeUnit].multiplier;
      handleChange(totalSeconds);
    }
  };

  const handleTimeUnitChange = (unit) => {
    setTimeUnit(unit);

    const err = validateTime(timeValue, unit);
    setError(err);

    if (!err) {
      const totalSeconds = timeValue * TIME_UNITS[unit].multiplier;
      handleChange(totalSeconds);
    }
  };

  const getTimeUnitOptions = () =>
    Object.entries(TIME_UNITS).map(([key, { label, min, max }]) => (
      <Option key={key} value={key}>
        {label}
      </Option>
    ));

  const getCurrentWaitTimeDisplay = () => {
    const totalSeconds = data?.operations?.delay || 0;
    if (totalSeconds === 0) return '';

    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);

    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);

    return parts.length > 0 ? `(${parts.join(' ')})` : '';
  };

  return (
    <>
      <div className="rounded-lg">
        <Text type="" className="block font-normal text-[14px]">
          <ClockCircleOutlined className="mr-2" />
          Set the waiting time before sending the follow-up message
        </Text>
        <p className="text-[0.800rem] mb-4 mt-1"><span className="underline font-medium">Note:</span> <span className="italic text-gray-500"> The follow ups message will be  sent only to the  previously delivered contacts.</span></p>

        <Row gutter={12} align="middle">
          <Col flex="auto">
            <Input
              type="number"
              min={TIME_UNITS[timeUnit].min}
              max={TIME_UNITS[timeUnit].max}
              value={timeValue}
              onChange={(e) => handleTimeValueChange(e.target.value)}
              className="w-full"
              status={error ? 'error' : ''}
              placeholder="Enter time"
            />
          </Col>
          <Col flex="200px">
            <Select
              value={timeUnit}
              onChange={handleTimeUnitChange}
              className="w-full [&_.ant-select-selector]:text-[12px] [&_.ant-select-item]:text-[12px]"
              popupClassName="[&_.ant-select-item]:text-[12px]"
            >
              {getTimeUnitOptions()}
            </Select>
          </Col>
        </Row>

        {/* <Row>
          <Col span={24}>
            <div className="flex flex-col mt-2">
              <Text type="secondary" className="text-xs">
                Wait time: 2 minutes - 7 days {getCurrentWaitTimeDisplay()}
              </Text>
            </div>
          </Col>
        </Row> */}

        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            className="mt-3"
            size="small"
          />
        )}

        <div className="mt-3 p-3 bg-blue-50 rounded border border-blue-200">
          <Text type="secondary" className="text-xs block">
            <strong>Usage Examples:</strong>
            <br />• 2 minutes – 59 minutes: For short delays
            <br />• 1 hour – 23 hours: For same-day follow-ups
            <br />• 1 day – 7 days: For long-term sequences
          </Text>
        </div>
      </div>
    </>
  );
};

export default FollowUps;
